import { FirebaseOptionsExt } from '../types/FirebaseOptionsExt';

const configCache = new Map<string, Promise<FirebaseOptionsExt>>();

const dummyFirebaseConfig: FirebaseOptionsExt = {
    apiKey: "dummy-api-key",
    authDomain: "dummy-project.firebaseapp.com",
    projectId: "dummy-project",
    storageBucket: "dummy-project.appspot.com",
    messagingSenderId: "123456789",
    appId: "1:123456789:web:abcdef",
    measurementId: "G-DUMMY",
    functionsDomain: "http://localhost:5001/dummy-project/us-central1",
    functionsUrl: {
        workshopsService: "http://localhost:5001/dummy-project/us-central1/workshopsService",
        workflowService: "http://localhost:5001/dummy-project/us-central1/workflowService",
        surveyManager: "http://localhost:5001/dummy-project/us-central1/surveyManager",
    },
    coachApiBase: "http://localhost:5001/dummy-project/us-central1"
};

/**
 * Simulates fetching Firebase configuration from a backend based on the selected environment.
 * @param environment 'Development' | 'QA' | 'Production' | 'Error' | 'Error500'
 */
export const getFirebaseConfig = (environment: string): Promise<FirebaseOptionsExt> => {
  if (configCache.has(environment)) {
    return configCache.get(environment)!;
  }

  const fetchPromise = (async () => {
    console.log(`[ConfigService] Fetching configuration for environment: ${environment}`);

    // Simulation of an error 500 when calling getFirebaseConfig
    if (environment === 'Error500') {
      await new Promise(resolve => setTimeout(resolve, 500));
      throw { status: 500, message: "Internal Server Error" };
    }

    if (environment === 'Error403') {
      await new Promise(resolve => setTimeout(resolve, 500));
      throw { status: 403, message: "Access Denied" };
    }

    let getFirebaseConfigUrl;

    switch (environment) {
      case 'Development':
        getFirebaseConfigUrl = import.meta.env.VITE_CONFIG_URL_DEV;
        break;
      case 'QA':
        getFirebaseConfigUrl = import.meta.env.VITE_CONFIG_URL_QA;
        break;
      case 'Production':
        getFirebaseConfigUrl = import.meta.env.VITE_CONFIG_URL_PROD;
        break;
      default:
        getFirebaseConfigUrl = import.meta.env.VITE_CONFIG_URL_DEV;
        break;
    }

    console.log("getFirebaseConfigUrl", getFirebaseConfigUrl);

    try {
      let data
      try {
        if(!getFirebaseConfigUrl) throw new Error("No URL provided, fallback to dummy")
        const response = await fetch(getFirebaseConfigUrl + "/getFirebaseConfig", {
          method: 'POST', // Use the appropriate method (GET, POST, etc.)
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ key: 'value' }) // Send data as a JSON stringified body
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        data = await response.json();
        console.log("Response from function:", data);
      } catch (error) {
        console.log("Returning dummy config as fallback:", error);
        data = dummyFirebaseConfig;
      }

      console.log('[ConfigService] Fetched config');

      return data as FirebaseOptionsExt;
    } catch (error) {
      console.error('[ConfigService] Failed to fetch config', error);
      throw error;
    }
  })();

  configCache.set(environment, fetchPromise);

  fetchPromise.catch(() => {
    configCache.delete(environment);
  });

  return fetchPromise;
};
